import React from "react";

// Source
import StoreRoute from "../../store/Route";
import StoreDocumentPreview from "../../store/custom/Document";
import StorePreference from "../../store/Preference";
import ControllerAlert from "../Alert";
import * as HelperSrc from "../../HelperSrc";
import * as ApiDocument from "../../api/custom/Document";
import * as ModelDocument from "../../model/custom/Document";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelPreference from "../../model/Preference";

export default class ControllerDocumentConfirm {
    // Variable
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeDocumentPreview: ModelDocument.Istore | undefined;
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementViewMain = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private dataMain = React.useRef<ModelDocument.Idata | undefined>(undefined);
    private language = React.useRef<string>("");

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getSelectOption: ModelDocument.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelDocument.IselectOption | undefined>>;

    getDataMain: ModelDocument.Idata | undefined;
    setDataMain: React.Dispatch<React.SetStateAction<ModelDocument.Idata | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeDocumentPreview = new StoreDocumentPreview();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelDocument.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getDataMain, setDataMain] = React.useState<ModelDocument.Idata | undefined>(this.dataMain.current);
        this.getDataMain = getDataMain;
        this.setDataMain = setDataMain;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_document_confirm", () => {
                this.initializeHtmlElement();
            });

            const historyState = window.history.state as Record<string, unknown> | undefined;
            const parameterList = historyState ? (historyState.parameterList as ModelDocument.Idata) : undefined;

            this.dataMain.current = parameterList;
            this.setDataMain(this.dataMain.current);

            this.apiSelectOption();

            HelperSrc.routerHistoryPopState({ temporaryData: this.dataMain.current });
        }, []);

        React.useEffect(() => {
            this.storageRouteData.current = this.storeRoute.storageGet();
        }, [this.storageRouteData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption();
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    clickBack = (): void => {
        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.documentPreview.path, {
                temporaryData: this.dataMain.current
            });
        }
    };

    clickSave = (): void => {
        if (this.dataMain.current) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                create: true
            }));

            ApiDocument.saveData(this.dataMain.current, (response) => {
                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        create: false
                    }));

                    return;
                }

                const result = response.data.result as unknown as ModelDocument.Istatus;

                if (Object.entries(result).length > 0) {
                    this.apiCheckTaskStatus(result);
                }
            });
        }
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_document_confirm");
        }
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiDocument.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.setSelectOption(response.data.result as unknown as ModelDocument.IselectOption);
        });
    };

    private apiCheckTaskStatus = (result: ModelDocument.Istatus): void => {
        const timeout = setTimeout(() => {
            clearTimeout(timeout);

            ApiDocument.checkTaskStatus(result.task_name, (responseStatus) => {
                if (responseStatus instanceof Error || responseStatus.message.error) {
                    if (responseStatus instanceof Error) {
                        HelperSrc.writeLog("error", responseStatus.message);
                    } else {
                        this.controllerAlert.show("error", responseStatus.message.error, false);
                    }

                    this.setApiLoadingList((prev) => ({
                        ...prev,
                        create: false
                    }));

                    return;
                }

                const resultStatus = responseStatus.data.result as unknown as ModelDocument.Istatus;

                if (Object.entries(resultStatus).length > 0) {
                    ApiDocument.downloadDocx(resultStatus.download_path, (responseDownload) => {
                        this.setApiLoadingList((prev) => ({
                            ...prev,
                            create: false
                        }));

                        if (responseDownload instanceof Error || responseDownload.message.error) {
                            if (responseDownload instanceof Error) {
                                HelperSrc.writeLog("error", responseDownload.message);
                            } else {
                                this.controllerAlert.show("error", responseDownload.message.error, false);
                            }

                            return;
                        }

                        const resultDownload = responseDownload.data.result as unknown as ModelDocument.Idownload;

                        if (Object.entries(resultDownload).length > 0) {
                            HelperSrc.downloadFromBrowser(resultDownload.file, resultDownload.filename);

                            if (this.storeDocumentPreview) {
                                this.storeDocumentPreview.storageRemove();
                            }

                            this.controllerAlert.show("success", responseDownload.message.success, true, true, true);
                        }
                    });
                } else {
                    this.apiCheckTaskStatus(result);
                }
            });
        }, HelperSrc.STATUS_TIMEOUT);
    };
}
